// Timetable.js
import React, { useState, useEffect } from "react";
import "./Timetable.css";

export default function Timetable({ departments }) {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const [selectedDeptIndex, setSelectedDeptIndex] = useState(null);
  const [selectedCourseIndex, setSelectedCourseIndex] = useState(null);

  const [periods, setPeriods] = useState([]);
  const [newPeriod, setNewPeriod] = useState({ label: "", time: "" });

  const [timetable, setTimetable] = useState({});

  // ---------- Modal states ----------
  const [periodModalOpen, setPeriodModalOpen] = useState(false);
  const [periodModalIndex, setPeriodModalIndex] = useState(null);
  const [periodModalLabel, setPeriodModalLabel] = useState("");
  const [periodModalTime, setPeriodModalTime] = useState("");
  const [periodDeleteConfirm, setPeriodDeleteConfirm] = useState(false);

  const [cellModalOpen, setCellModalOpen] = useState(false);
  const [cellModalDay, setCellModalDay] = useState(null);
  const [cellModalPeriodLabel, setCellModalPeriodLabel] = useState("");
  const [cellModalSubject, setCellModalSubject] = useState("");
  const [cellModalSubjectCode, setCellModalSubjectCode] = useState("");
  const [cellModalFacultyName, setCellModalFacultyName] = useState("");
  const [cellModalFacultyId, setCellModalFacultyId] = useState("");
  const [cellDeleteConfirm, setCellDeleteConfirm] = useState(false);

  // ---------- Keys helpers ----------
  const getCourseCode = (course) => {
    if (!course) return "";
    return typeof course === "string" ? course : course.code;
  };

  const generateCourseKey = (deptName, course) =>
    `${deptName}_${getCourseCode(course)}`;

  // When dept/course changes -> load saved timetable & periods
  useEffect(() => {
    if (selectedDeptIndex !== null && selectedCourseIndex !== null) {
      const dept = departments[selectedDeptIndex];
      const course = dept.courses[selectedCourseIndex];
      const key = generateCourseKey(dept.name, course);

      const savedTimetable =
        JSON.parse(localStorage.getItem(`${key}_timetable`)) || {};
      const savedPeriods =
        JSON.parse(localStorage.getItem(`${key}_periods`)) || [];

      setTimetable(savedTimetable);
      setPeriods(savedPeriods);
    } else {
      setTimetable({});
      setPeriods([]);
    }
  }, [selectedDeptIndex, selectedCourseIndex, departments]);

  // Save helpers
  const saveTimetable = (dept, course, data) => {
    const key = generateCourseKey(dept.name, course);
    localStorage.setItem(`${key}_timetable`, JSON.stringify(data));
  };

  const savePeriods = (dept, course, data) => {
    const key = generateCourseKey(dept.name, course);
    localStorage.setItem(`${key}_periods`, JSON.stringify(data));
  };

  // ---------- Period operations ----------
  const handleAddPeriod = () => {
    if (!newPeriod.label || !newPeriod.time) return alert("Fill both fields");
    const updated = [...periods, newPeriod];
    setPeriods(updated);

    if (selectedDeptIndex !== null && selectedCourseIndex !== null) {
      const dept = departments[selectedDeptIndex];
      const course = dept.courses[selectedCourseIndex];
      savePeriods(dept, course, updated);
    }

    setNewPeriod({ label: "", time: "" });
  };

  const openPeriodModal = (idx) => {
    setPeriodModalIndex(idx);
    setPeriodModalLabel(periods[idx].label);
    setPeriodModalTime(periods[idx].time);
    setPeriodDeleteConfirm(false);
    setPeriodModalOpen(true);
  };

  const closePeriodModal = () => {
    setPeriodModalOpen(false);
    setPeriodModalIndex(null);
    setPeriodDeleteConfirm(false);
  };

  const savePeriodModal = () => {
    if (periodModalIndex === null) return closePeriodModal();
    const oldLabel = periods[periodModalIndex].label;
    const newLabel = periodModalLabel.trim();
    const newTime = periodModalTime.trim();
    if (!newLabel || !newTime) return alert("Label and time are required");

    const updated = [...periods];
    updated[periodModalIndex] = { label: newLabel, time: newTime };
    setPeriods(updated);

    if (selectedDeptIndex !== null && selectedCourseIndex !== null) {
      const dept = departments[selectedDeptIndex];
      const course = dept.courses[selectedCourseIndex];
      savePeriods(dept, course, updated);

      if (oldLabel !== newLabel) {
        const updatedTimetable = { ...timetable };
        days.forEach((day) => {
          if (updatedTimetable[day] && updatedTimetable[day][oldLabel]) {
            updatedTimetable[day][newLabel] = {
              ...updatedTimetable[day][oldLabel],
            };
            delete updatedTimetable[day][oldLabel];
          }
        });
        setTimetable(updatedTimetable);
        saveTimetable(dept, course, updatedTimetable);
      }
    }

    closePeriodModal();
  };

  const deletePeriodFromModal = () => {
    if (periodModalIndex === null) return closePeriodModal();

    const idx = periodModalIndex;
    const labelToRemove = periods[idx].label;
    const updated = periods.filter((_, i) => i !== idx);
    setPeriods(updated);

    if (selectedDeptIndex !== null && selectedCourseIndex !== null) {
      const dept = departments[selectedDeptIndex];
      const course = dept.courses[selectedCourseIndex];
      savePeriods(dept, course, updated);

      const updatedTimetable = { ...timetable };
      days.forEach((day) => {
        if (updatedTimetable[day]) {
          delete updatedTimetable[day][labelToRemove];
        }
      });
      setTimetable(updatedTimetable);
      saveTimetable(dept, course, updatedTimetable);
    }

    closePeriodModal();
  };

  // ---------- Cell operations ----------
  const openCellModal = (day, periodLabel) => {
    setCellModalDay(day);
    setCellModalPeriodLabel(periodLabel);

    const existing = timetable?.[day]?.[periodLabel] || {};
    setCellModalSubject(existing.subject || "");
    setCellModalSubjectCode(existing.subjectCode || "");
    setCellModalFacultyName(existing.facultyName || "");
    setCellModalFacultyId(existing.facultyId || "");
    setCellDeleteConfirm(false);
    setCellModalOpen(true);
  };

  const closeCellModal = () => {
    setCellModalOpen(false);
    setCellModalDay(null);
    setCellModalPeriodLabel("");
    setCellDeleteConfirm(false);
  };

  const saveCellModal = () => {
    if (selectedDeptIndex === null || selectedCourseIndex === null) {
      closeCellModal();
      return;
    }
    const dept = departments[selectedDeptIndex];
    const course = dept.courses[selectedCourseIndex];
    const courseKey = generateCourseKey(dept.name, course);

    const updated = { ...timetable };
    if (!updated[cellModalDay]) updated[cellModalDay] = {};
    updated[cellModalDay][cellModalPeriodLabel] = {
      subject: cellModalSubject,
      subjectCode: cellModalSubjectCode,
      facultyName: cellModalFacultyName,
      facultyId: cellModalFacultyId,
      deptName: dept.name,
      courseKey,
    };

    setTimetable(updated);
    saveTimetable(dept, course, updated);
    closeCellModal();
  };

  const deleteCellFromModal = () => {
    if (selectedDeptIndex === null || selectedCourseIndex === null) {
      closeCellModal();
      return;
    }
    const dept = departments[selectedDeptIndex];
    const course = dept.courses[selectedCourseIndex];

    const updated = { ...timetable };
    if (updated[cellModalDay]) {
      delete updated[cellModalDay][cellModalPeriodLabel];
    }

    setTimetable(updated);
    saveTimetable(dept, course, updated);
    closeCellModal();
  };

  // ---------- rendering ----------
  return (
    <div className="timetable-section">
      <h2>📅 Timetable Management</h2>

      {/* Department Selection */}
      <div>
        <h3>Select Department</h3>
        <div className="dept-buttons">
          {departments.map((dept, dIdx) => (
            <button
              key={dIdx}
              className={`dept-btn ${selectedDeptIndex === dIdx ? "active" : ""}`}
              onClick={() => {
                setSelectedDeptIndex(dIdx);
                setSelectedCourseIndex(null);
              }}
            >
              {dept.name}
            </button>
          ))}
        </div>
      </div>

      {/* Course Selection */}
      {selectedDeptIndex !== null && (
        <div>
          <h3>Select Course</h3>
          <div className="course-buttons">
            {departments[selectedDeptIndex].courses.map((c, cIdx) => (
              <button
                key={cIdx}
                className={`course-btn ${selectedCourseIndex === cIdx ? "active" : ""}`}
                onClick={() => setSelectedCourseIndex(cIdx)}
              >
                {c.name} ({c.code})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Timetable Grid */}
      {selectedDeptIndex !== null && selectedCourseIndex !== null && (
        <div className="timetable-grid">
          <h3>
            Editing Timetable for{" "}
            {departments[selectedDeptIndex].courses[selectedCourseIndex].name}
          </h3>

          {/* Add Periods */}
          <div className="period-form">
            <input
              type="text"
              placeholder="Period Label (I, II)"
              value={newPeriod.label}
              onChange={(e) =>
                setNewPeriod({ ...newPeriod, label: e.target.value })
              }
            />
            <input
              type="text"
              placeholder="Time (e.g. 9-10 AM)"
              value={newPeriod.time}
              onChange={(e) =>
                setNewPeriod({ ...newPeriod, time: e.target.value })
              }
            />
            <button onClick={handleAddPeriod}>➕ Add Period</button>
          </div>

          <table className="timetable-table">
            <thead>
              <tr>
                <th>Day</th>
                {periods.map((p, idx) => (
                  <th key={idx}>
                    <div
                      className="period-header"
                      onClick={() => openPeriodModal(idx)}
                      role="button"
                    >
                      <span className="period-label">{p.label}</span>
                      <br />
                      <span className="period-time">({p.time})</span>
                      <div className="period-hint">Click to edit / delete</div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map((day) => (
                <tr key={day}>
                  <td className="day-cell">{day}</td>
                  {periods.map((p, i) => {
                    const cellData = timetable[day]?.[p.label];
                    return (
                      <td
                        key={i}
                        onClick={() => openCellModal(day, p.label)}
                        className="timetable-cell"
                        role="button"
                        title="Click to edit this cell"
                      >
                        {cellData ? (
                          <div>
                            <strong>
                              {cellData.subject} ({cellData.subjectCode})
                            </strong>
                            <br />
                            {cellData.facultyName} ({cellData.facultyId})
                          </div>
                        ) : (
                          <div className="empty-cell">-</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== Period Modal ===== */}
      {periodModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit Period</h3>
            <label>Label</label>
            <input
              value={periodModalLabel}
              onChange={(e) => setPeriodModalLabel(e.target.value)}
            />
            <label>Time</label>
            <input
              value={periodModalTime}
              onChange={(e) => setPeriodModalTime(e.target.value)}
            />

            <div className="modal-actions">
              <button className="btn btn-save" onClick={savePeriodModal}>
                Save
              </button>
              <button className="btn btn-cancel" onClick={closePeriodModal}>
                Cancel
              </button>

              {periodDeleteConfirm ? (
                <>
                  <span className="delete-confirm-text">Confirm delete?</span>
                  <button
                    className="btn btn-danger"
                    onClick={deletePeriodFromModal}
                  >
                    Yes, Delete
                  </button>
                  <button
                    className="btn btn-cancel"
                    onClick={() => setPeriodDeleteConfirm(false)}
                  >
                    No
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-danger"
                  onClick={() => setPeriodDeleteConfirm(true)}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== Cell Modal ===== */}
      {cellModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit Timetable Entry</h3>

            <label>Subject</label>
            <input
              value={cellModalSubject}
              onChange={(e) => setCellModalSubject(e.target.value)}
            />

            <label>Subject Code</label>
            <input
              value={cellModalSubjectCode}
              onChange={(e) => setCellModalSubjectCode(e.target.value)}
            />

            <label>Faculty Name</label>
            <input
              value={cellModalFacultyName}
              onChange={(e) => setCellModalFacultyName(e.target.value)}
            />

            <label>Faculty ID</label>
            <input
              value={cellModalFacultyId}
              onChange={(e) => setCellModalFacultyId(e.target.value)}
            />

            <div className="modal-actions">
              <button className="btn btn-save" onClick={saveCellModal}>
                Save
              </button>
              <button className="btn btn-cancel" onClick={closeCellModal}>
                Cancel
              </button>

              {cellDeleteConfirm ? (
                <>
                  <span className="delete-confirm-text">Confirm delete?</span>
                  <button
                    className="btn btn-danger"
                    onClick={deleteCellFromModal}
                  >
                    Yes, Delete
                  </button>
                  <button
                    className="btn btn-cancel"
                    onClick={() => setCellDeleteConfirm(false)}
                  >
                    No
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-danger"
                  onClick={() => setCellDeleteConfirm(true)}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
